<template>
  <div class="bottom-box" :class="wrapClass">
    <div class="left-wrap">
      <woot-button
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        :title="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        icon="emoji"
        emoji="😊"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        class="hide-on-mobile"
        @click="toggleEmojiPicker"
        :is-disabled="!enableFastReply"
      />
      <file-upload
        ref="upload"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="true"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
        :disabled="!enableFastReply"
      >
        <woot-button
          v-if="showAttachButton"
          class-names="button--upload"
          :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="attach"
          emoji="📎"
          color-scheme="secondary"
          variant="smooth"
          size="small"
          :is-disabled="!enableFastReply"
        />
      </file-upload>

      <woot-button
        v-if="showAudioRecorderButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :icon="!isRecordingAudio ? 'microphone' : 'microphone-off'"
        emoji="🎤"
        :color-scheme="!isRecordingAudio ? 'secondary' : 'alert'"
        variant="smooth"
        size="small"
        @click="toggleAudioRecorder"
        :is-disabled="!enableFastReply"
      />
      <woot-button
        v-if="showEditorToggle && !isObserverMode"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        icon="quote"
        emoji="🖊️"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="$emit('toggle-editor')"
        :is-disabled="!enableFastReply"
      />
      <woot-button
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        emoji="🎤"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="toggleAudioRecorderPlayPause"
        :is-disabled="!enableFastReply"
      >
        <span>{{ recordingAudioDurationText }}</span>
      </woot-button>
      <woot-button
        v-if="showMessageSignatureButton"
        v-tooltip.top-end="signatureToggleTooltip"
        icon="signature"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        :title="signatureToggleTooltip"
        @click="toggleMessageSignature"
        :is-disabled="isSendDisabled"
      />
      <woot-button
        v-if="hasWhatsappTemplates && !isObserverMode"
        v-tooltip.top-end="whatsappTemplatesTooltips"
        icon="whatsapp"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        :disabled="blockSendingTemplates"
        @click="selectWhatsappTemplate"
        :class="[
          {
            'whatsapp-template-expanded': !currentChat.can_reply,
            'whatsapp-template-highlight': !currentChat.can_reply,
          },
        ]"
      >
        <template v-if="!currentChat.can_reply">
          <span>{{
            $t('CONVERSATION.REPLYBOX.WHATSAPP_TEMPLATES_BUTTON_TEXT')
          }}</span>
        </template>
      </woot-button>

      <video-call-button
        v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
        :conversation-id="conversationId"
      />
      <woot-button
        v-tooltip.top-end="$t('SCHEDULED_MESSAGE.HEADER')"
        :title="$t('SCHEDULED_MESSAGE.HEADER')"
        class="hide-on-mobile position-relative"
        :class="{ scheduled: !!hasScheduledMessages }"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="toggleScheduledMessage"
      >
        <fluent-icon icon="calendar-clock" />
      </woot-button>
      <div v-if="!isObserverMode" class="button-container">
        <woot-button
          v-tooltip.top-end="$t('CANNED_MGMT.HEADER')"
          :title="$t('CANNED_MGMT.HEADER')"
          class="hide-on-mobile"
          color-scheme="secondary"
          variant="smooth"
          size="small"
          @click="selectCannedResponse"
        >
          <span class="cmd-key">⌘</span>
        </woot-button>
      </div>
      <AIAssistanceButton
        class="hide-on-mobile"
        :conversation-id="conversationId"
        :is-private-note="isOnPrivateNote"
        :message="message"
        @replace-text="replaceText"
      />
      <transition name="modal-fade">
        <div
          v-show="$refs.upload && $refs.upload.dropActive"
          class="modal-mask"
        >
          <fluent-icon icon="cloud-backup" />
          <h4 class="page-sub-title">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
    </div>
    <div class="right-wrap">
      <woot-button
        size="small"
        :class-names="buttonClass"
        :is-disabled="isSendDisabled || isSending"
        :is-loading="isSending"
        @click="handleSendClick"
      >
        {{ sendButtonText }}
      </woot-button>
    </div>

    <div
      v-if="showConfirmModal"
      class="custom-modal-overlay"
      @click.self="closeConfirmModal"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('CONVERSATION.REPLYBOX.CONFIRM_PRIVATE_NOTE_TITLE')"
    >
      <div class="custom-modal" ref="confirmModal" @keydown.stop>
        <header class="custom-modal__header">
          <h3 class="custom-modal__title">
            {{ $t('CONVERSATION.REPLYBOX.CONFIRM_PRIVATE_NOTE_TITLE') }}
          </h3>
          <button
            type="button"
            class="custom-modal__close"
            aria-label="Fechar"
            @click="closeConfirmModal"
          >
            ✕
          </button>
        </header>

        <div class="custom-modal__body">
          <p v-html="$t('CONVERSATION.REPLYBOX.CONFIRM_PRIVATE_NOTE_TEXT')"></p>
        </div>

        <footer class="custom-modal__footer">
          <woot-button
            color-scheme="primary"
            @click="confirmPrivateNoteSend"
            :is-disabled="isSendDisabled"
          >
            {{ $t('CONVERSATION.REPLYBOX.CONFIRM') }}
          </woot-button>
          <woot-button
            color-scheme="secondary"
            variant="smooth"
            @click="closeConfirmModal"
            :is-disabled="false"
          >
            {{ $t('CONVERSATION.REPLYBOX.CANCEL') }}
          </woot-button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import { hasPressedAltAndAKey } from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP,
} from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton';
import AIAssistanceButton from '../AIAssistanceButton.vue';
import { REPLY_EDITOR_MODES } from './constants';
import { mapGetters } from 'vuex';

export default {
  name: 'ReplyBottomPanel',
  components: { FileUpload, VideoCallButton, AIAssistanceButton },
  mixins: [eventListenerMixins, uiSettingsMixin, inboxMixin],
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '',
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    showEmojiPicker: {
      type: Boolean,
      default: false,
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    toggleScheduledMessage: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    showEditorToggle: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    hasWhatsappTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    message: {
      type: String,
      default: '',
    },
    enableFastReply: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showConfirmModal: false,
      lastFocusedElementBeforeModal: null
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentChat: 'getSelectedChat',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isObserverMode: 'observerMode/isObserverMode',
      getScheduledList: 'scheduledMessages/getScheduledList',
      conversationsUiFlags: 'contactConversations/getUIFlags',
      messageUiFlags: 'getMessageUIFlags',
    }),
    hasScheduledMessages() {
      return (
        Array.isArray(this.getScheduledList) && this.getScheduledList.length > 0
      );
    },
    isNote() {
      return this.mode === REPLY_EDITOR_MODES.NOTE;
    },
    wrapClass() {
      return { 'is-note-mode': this.isNote };
    },
    buttonClass() {
      return { warning: this.isNote };
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isObserverMode) return false;
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
        navigator.userAgent
      );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) &&
        this.showAudioRecorder &&
        !isSafari
      );
    },
    showAudioPlayStopButton() {
      if (this.isObserverMode) return false;
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    allowedFileTypes() {
      return this.isATwilioWhatsAppChannel
        ? ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP
        : ALLOWED_FILE_TYPES;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        case 'playing':
          return 'microphone-pause';
        case 'paused':
        case 'stopped':
          return 'microphone-play';
        default:
          return 'microphone-stop';
      }
    },
    showMessageSignatureButton() {
      if (this.isObserverMode) return false;
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    sendWithSignature() {
      return this.uiSettings.send_with_signature;
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    blockSendingTemplates() {
      return this.currentChat.can_reply === true;
    },
    whatsappTemplatesTooltips() {
      return this.blockSendingTemplates
        ? this.$t('WHATSAPP_TEMPLATES.TOOLTIPS.BLOCK_TEMPLATE')
        : this.$t('WHATSAPP_TEMPLATES.TOOLTIPS.WHATSAPP_TEMPLATE');
    },
    isSending(){
      return this.messageUiFlags.isCreating || this.conversationsUiFlags.isCreating;
    }
  },
  mounted() {
    ActiveStorage.start();
    document.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleGlobalKeydown);
  },
  methods: {
    selectWhatsappTemplate() {
      if (this.blockSendingTemplates) return;
      this.$emit('selectWhatsappTemplate');
    },
    handleKeyEvents(e) {
      if (hasPressedAltAndAKey(e)) {
        this.$refs.upload.$children[1].$el.click();
      }
    },
    toggleMessageSignature() {
      this.updateUISettings({
        send_with_signature: !this.sendWithSignature,
      });
    },
    replaceText(text) {
      this.$emit('replace-text', text);
    },
    selectCannedResponse() {
      this.$emit('selectCannedResponse');
    },
    handleSendClick() {
      if (this.isSending) return;
      if (this.isOnPrivateNote) {
        this.openConfirmModal();
      } else {
        this.onSend();
      }
    },
    openConfirmModal() {
      this.lastFocusedElementBeforeModal = document.activeElement;
      this.showConfirmModal = true;
    },
    closeConfirmModal() {
      this.showConfirmModal = false;
    },
    confirmPrivateNoteSend() {
      this.showConfirmModal = false;

      this.$nextTick(() => {
        this.onSend();
      });
    },
    handleGlobalKeydown(e) {
      if (this.showConfirmModal && e.key === 'Escape') this.closeConfirmModal();
      if (this.showConfirmModal && e.key === 'Enter')
        this.confirmPrivateNoteSend();
    },
  },
};
</script>

<style lang="scss" scoped>
.bottom-box {
  display: flex;
  justify-content: space-between;
  padding: var(--space-slab) var(--space-normal);

  &.is-note-mode {
    background: var(--y-50);
  }

  .hide-on-mobile {
    @media screen and (max-width: 768px) {
      display: none !important;
    }
  }
}

button.scheduled {
  background-color: #ffebd3 !important;
}

.button-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.beta {
  padding: 1px 10px;
  min-width: 36px;
  text-align: center;
  font-size: 9px;
  font-weight: var(--font-weight-medium);
  line-height: 11px;
  border: 1px solid var(--g-700);
  border-radius: 999px;
  background: var(--g-800);
  color: #fff;

  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-56%);
  white-space: nowrap;
}

.hide-on-mobile.position-relative {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.left-wrap .button {
  margin-right: var(--space-small);
}

.left-wrap {
  align-items: center;
  display: flex;
}

.right-wrap {
  display: flex;
}

::v-deep .file-uploads {
  label {
    cursor: pointer;
  }
  &:hover .button {
    background: var(--s-100);
  }
}

.custom-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 10, 10, 0.35);
  padding: 24px;
}

.custom-modal {
  width: 100%;
  max-width: 720px;
  background: var(--white, #fff);
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(15, 15, 15, 0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  outline: none;
}

.custom-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--s-100);
  background: var(--white);
}

.custom-modal__title {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 700;
  color: var(--s-900);
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-modal__close {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.3rem;
  line-height: 1;
  color: var(--s-600);
  padding: 6px;
  border-radius: 6px;
  transition: background 0.2s ease;
}

.custom-modal__close:hover {
  background: var(--s-100);
}

.custom-modal__body {
  padding: 22px 24px;
  color: var(--s-700);
  font-size: 0.97rem;
  line-height: 1.5;
}

.custom-modal__footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid var(--s-100);
  background: var(--white);
}

.custom-modal__footer .woot-button {
  min-width: 110px;
  height: 36px;
  font-weight: var(--font-weight-medium);
  font-size: 0.95rem;
}

@media screen and (max-width: 480px) {
  .custom-modal {
    max-width: 420px;
    border-radius: 10px;
  }

  .custom-modal__title {
    font-size: 1.2rem;
  }
}

.modal-mask {
  color: var(--s-600);
  background: var(--white-transparent);
  flex-direction: column;
}

.page-sub-title {
  color: var(--s-600);
}

.icon {
  font-size: 8rem;
}

.cmd-key {
  font-size: 2.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 21px;
  height: 21px;
}

.whatsapp-template-expanded span {
  margin-left: 4px;
  display: inline-flex;
  align-items: center;
}

@keyframes pulse-red-only {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.25);
  }
  70% {
    box-shadow: 0 0 6px 2px rgba(255, 0, 0, 0.4);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
  }
}

.whatsapp-template-highlight {
  animation: pulse-red-only 3s infinite ease-out;
  border-radius: 6px;
}
</style>
